import { CalendarOutlined } from "@ant-design/icons"
import { Button, Card, Empty, Segmented, Space, Spin, Tag, Typography } from "antd"
import React, { FC, useContext, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory, useLocation } from "react-router-dom"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import formatDate from "../../helpers/formatDate"
import { getTextColor } from "../../helpers/getTextColor"
import { getStoredBookerId } from "../../helpers/getStoredBookerId"
import { getParamObject } from "../../helpers/iframeParams"
import { AccountBookerBookingsQuery, useAccountBookerBookingsQuery } from "../../generated/graphql"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import { URL_ACCOUNT_BOOKINGS, URL_COURSE_DETAILS, URL_WIDERRUF } from "../../models/url"
import { SubHeader } from "./AccountDashboard"
import { STATUS_TAG, WithAttendeeName, findNextOpenOnlinePayment, flattenBookerBookings, getBookingStatus, isPastBooking, lessonTimes, safeDate } from "./bookingStatus"
import CalendarFeedModal from "./CalendarFeedModal"
import css from "./AccountBookings.module.css"

const { Text } = Typography

type BookerAttendee = NonNullable<NonNullable<AccountBookerBookingsQuery["booker"]>["attendees"]>[number]
type Booking = WithAttendeeName<NonNullable<NonNullable<BookerAttendee>["bookings"]>[number]>

/** Sortierwert (ms) für aktive Buchungen: nächster anstehender Termin, sonst frühester Termin. */
const activeSortValue = (booking: Booking, nowMs: number): number => {
    const starts = lessonTimes(booking, "startDateTime")
    const upcoming = starts.filter((t) => t >= nowMs)
    if (upcoming.length) return Math.min(...upcoming)
    return starts.length ? Math.min(...starts) : Infinity
}

/** Sortierwert (ms) für vergangene Buchungen: letzter Termin. */
const pastSortValue = (booking: Booking): number => {
    const ends = lessonTimes(booking, "endDateTime")
    return ends.length ? Math.max(...ends) : -Infinity
}

/**
 * Link auf die Kursliste, gefiltert auf die Folgekurse dieses Kurses (Issue #770).
 *
 * Bewusst ein echter Seitenwechsel statt history.push: `prerequisiteCourseId`
 * liest die App nur EINMAL beim Start (index.tsx → App → CourseFilterProvider).
 *
 * Die Embed-Parameter (parentUrl, Sprache, custom CSS) kommen über dieselbe
 * Funktion wie beim App-Start: Alt-Embeds tragen sie im Hash statt im
 * Search-String, und nur `getParamObject` kennt beide Quellen samt Vorrang.
 * Der Hash entfällt im Ergebnis — damit landet der Aufruf auf der Kursliste.
 */
export const followUpCoursesHref = (courseId: string): string => {
    const params = new URLSearchParams({ ...getParamObject([]), prerequisiteCourseId: courseId })
    return `${window.location.pathname}?${params.toString()}`
}

/** Buchungs-Kachel — auch vom Dashboard ("Als Nächstes") wiederverwendet. */
export const BookingCard: FC<{ booking: Booking; nowMs: number; withdrawalEnabled: boolean }> = ({ booking, nowMs, withdrawalEnabled }) => {
    const { t } = useTranslation()
    // fromUrl aus der echten aktuellen Location: Rücksprung landet dort, wo die
    // Kachel stand (Buchungsliste inkl. Tab-Query oder Dashboard)
    const cardLocation = useLocation()
    const fromUrl = { pathname: cardLocation.pathname, search: cardLocation.search }
    const status = getBookingStatus(booking, new Date(nowMs))
    const tag = STATUS_TAG[status]
    const past = isPastBooking(booking, new Date(nowMs))
    const course = booking.course
    // Offene Online-Rate (Mollie) → dezenter "Zahlung ausstehend"-Badge neben dem Status-Tag
    const pendingOnlinePayment = findNextOpenOnlinePayment(booking)

    const firstStart = lessonTimes(booking, "startDateTime")
    const earliestStart = firstStart.length ? Math.min(...firstStart) : undefined
    const lessonCount = booking.courseLessons.length
    const firstLocation = booking.courseLessons.find((l) => l.location?.name || l.location?.city)?.location
    const lessonsLabel = lessonCount === 1 ? t("account.bookings.lessonsCountOne", { count: lessonCount, defaultValue: "{{count}} Termin" }) : t("account.bookings.lessonsCountOther", { count: lessonCount, defaultValue: "{{count}} Termine" })

    const bookingReference = `${booking.bookingNumberPrefix ?? ""}${booking.bookingNumber ?? ""}`

    // Header in der Kursart-Farbe, gleiche Kontrast-Logik wie in den Kursdetails
    const courseTypeColor = course?.courseType?.color ?? undefined
    const headerTextColor = courseTypeColor ? getTextColor({ value: courseTypeColor }) : undefined

    return (
        <Card
            size='small'
            className={css.card}
            data-testid='account-booking-card'
            title={<Text strong style={{ color: headerTextColor }}>{course?.courseType?.name ?? course?.prefixedCourseNumber ?? t("account.bookings.unknownCourse", "Kurs")}</Text>}
            extra={
                <Space size={4}>
                    {pendingOnlinePayment && <Tag color='gold' data-testid='account-booking-payment-pending'>{t("account.payment.pending", "Zahlung ausstehend")}</Tag>}
                    <Tag color={tag.color}>{t(tag.key, tag.fallback)}</Tag>
                </Space>
            }
            headStyle={courseTypeColor ? { background: courseTypeColor } : undefined}
        >
            {course?.prefixedCourseNumber && <Text type='secondary'>{course.prefixedCourseNumber}</Text>}
            <Text type='secondary' className={css.line}>
                {t("account.bookings.bookingMeta", {
                    number: bookingReference,
                    // safeDate: fehlendes/kaputtes createDate darf die Karte nicht crashen (Invalid time value)
                    date: (() => {
                        const created = safeDate(booking.createDate)
                        return created ? formatDate(created, "dd.MM.yyyy") : "—"
                    })(),
                    defaultValue: "Buchung {{number}} · gebucht am {{date}}",
                })}
            </Text>
            {earliestStart !== undefined && (
                <Text type='secondary' className={css.line}>
                    {`${t("account.bookings.startsOn", { date: formatDate(new Date(earliestStart), "EE dd.MM.yyyy"), defaultValue: "ab {{date}}" })} · ${lessonsLabel}`}
                </Text>
            )}
            {firstLocation && (
                <Text type='secondary' className={css.line}>
                    {[firstLocation.name, firstLocation.city].filter(Boolean).join(", ")}
                </Text>
            )}
            {booking.attendeeName && (
                <div className={css.line}>
                    <Tag color='blue'>{booking.attendeeName}</Tag>
                </div>
            )}
            <Space className={css.actions} size='middle'>
                <Link
                    // fromUrl: Zurück-Pfeil der Detailseite kehrt zur Ursprungsseite zurück (Liste inkl. Tab oder Dashboard)
                    to={{
                        pathname: `${URL_ACCOUNT_BOOKINGS}/${booking.id}`,
                        state: { fromUrl },
                    }}
                    data-testid='account-booking-details'
                >
                    {t("account.bookings.bookingDetails", "Buchungsdetails")}
                </Link>
                {course?.id && (
                    <Link
                        // fromActiveBooking: Kursdetails benennen den Buchen-Button um ("Weiteren Teilnehmer buchen")
                        to={{
                            pathname: URL_COURSE_DETAILS.replace(":id", course.id),
                            state: { fromUrl, ...(!past && { fromActiveBooking: true }) },
                        }}
                        data-testid='account-booking-course-details'
                    >
                        {past ? t("account.bookings.bookAgain", "Erneut buchen") : t("account.bookings.courseDetails", "Kursdetails")}
                    </Link>
                )}
                {course?.id && (course.isPrerequisiteForCourseCount ?? 0) > 0 && (
                    <a href={followUpCoursesHref(course.id)} data-testid='account-booking-follow-up-courses'>
                        {t("account.bookings.followUpCourses", "Folgekurse anzeigen")}
                    </a>
                )}
                {!past && status === "booked" && withdrawalEnabled && (
                    <Link to={{ pathname: URL_WIDERRUF, search: `?bookingNumber=${encodeURIComponent(bookingReference)}` }} data-testid='account-booking-withdraw'>
                        {t("account.bookings.withdraw", "Widerruf")}
                    </Link>
                )}
            </Space>
        </Card>
    )
}

const AccountBookings: FC = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const [tab, setTab] = useState<"active" | "past">(new URLSearchParams(location.search).get("tab") === "past" ? "past" : "active")
    const withdrawalEnabled = useContext(SettingsContext).withdrawalButtonEnabled === true
    const calendarFeedEnabled = useContext(SettingsContext).calendarFeedEnabled === true
    const bookerId = getStoredBookerId()
    const [calendarModalOpen, setCalendarModalOpen] = useState(false)

    const { data, loading, error, refetch } = useAccountBookerBookingsQuery({
        skip: !bookerId,
        variables: { bookerId: bookerId ?? "" },
        fetchPolicy: "cache-and-network",
    })

    const nowMs = Date.now()
    const bookings = useMemo(() => flattenBookerBookings(data?.booker?.attendees), [data])
    const now = useMemo(() => new Date(nowMs), [nowMs])

    const { active, past } = useMemo(() => {
        const activeList = bookings.filter((b) => !isPastBooking(b, now)).sort((a, b) => activeSortValue(a, nowMs) - activeSortValue(b, nowMs))
        const pastList = bookings.filter((b) => isPastBooking(b, now)).sort((a, b) => pastSortValue(b) - pastSortValue(a))
        return { active: activeList, past: pastList }
    }, [bookings, now, nowMs])

    const title = t("account.bookings.title", "Meine Buchungen")

    if (loading && bookings.length === 0) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <div className={css.loading} data-testid='account-bookings-loading'>
                    <Spin spinning />
                </div>
            </div>
        )
    }

    if (error && bookings.length === 0) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <QueryErrorAlert testId='account-bookings-error' message={t("account.loadError", "Dein Benutzerkonto konnte nicht geladen werden.")} onRetry={() => refetch()} />
            </div>
        )
    }

    const list = tab === "active" ? active : past
    const emptyText = tab === "active" ? t("account.bookings.empty.active", "Keine aktiven Buchungen") : t("account.bookings.empty.past", "Keine vergangenen Buchungen")

    return (
        <div className={css.page} data-testid='account-bookings'>
            <SubHeader title={title} />
            {calendarFeedEnabled && bookerId && (
                <div className={css.calendarRow}>
                    <Button type='primary' block icon={<CalendarOutlined />} onClick={() => setCalendarModalOpen(true)} data-testid='account-calendar-feed-button'>
                        {t("account.calendarFeed.subscribe", "Termine im Kalender abonnieren")}
                    </Button>
                    <Text type='secondary' className={css.calendarHint}>
                        {t("account.calendarFeed.info", "Alle Kurstermine automatisch in deinem Kalender (Apple, Google, Outlook) — immer aktuell.")}
                    </Text>
                    <CalendarFeedModal open={calendarModalOpen} onClose={() => setCalendarModalOpen(false)} />
                </div>
            )}
            <Segmented
                block
                className={css.segmented}
                value={tab}
                onChange={(value) => {
                    const next = value as "active" | "past"
                    setTab(next)
                    // Tab in der URL halten (replace: kein History-Eintrag), damit auch Browser-Zurück ihn wiederherstellt
                    history.replace({ pathname: location.pathname, search: next === "past" ? "?tab=past" : "", state: location.state })
                }}
                options={[
                    { label: `${t("account.bookings.active", "Aktiv")} (${active.length})`, value: "active" },
                    { label: `${t("account.bookings.past", "Vergangen")} (${past.length})`, value: "past" },
                ]}
            />
            {list.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} data-testid='account-bookings-empty' />
            ) : (
                <div className={css.list}>
                    {list.map((booking) => (
                        <BookingCard key={booking.id} booking={booking} nowMs={nowMs} withdrawalEnabled={withdrawalEnabled} />
                    ))}
                </div>
            )}
        </div>
    )
}

export default AccountBookings
